package link.erpango.quip.data.remote.ktor

import io.ktor.client.HttpClient
import io.ktor.client.call.ReceivePipelineException
import io.ktor.client.plugins.ResponseException
import io.ktor.client.plugins.timeout
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.request
import io.ktor.client.statement.bodyAsText
import io.ktor.http.encodedPath
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.KSerializer
import kotlinx.serialization.SerializationException
import kotlinx.serialization.StringFormat
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.utils.FileUtils

class HttpRequest(

    override val httpClient: HttpClient,
    override val request: HttpRequestBuilder,
    override val json: StringFormat

): IHttpRequest {

    private val PARAM_DELIMITER = "="

    /**
     * Extrae el mensaje de error del cuerpo de la respuesta.
     *
     * Nunca devuelve el cuerpo crudo: si no es un JSON con `error`, `message` o `detail`
     * (por ejemplo la página HTML de un 404 de nginx) se cae a un mensaje genérico con el
     * código de estado. Volcar el cuerpo tal cual acababa pintando el HTML entero en la UI.
     */
    private fun extractBackendErrorMessage(responseBody: String, statusCode: Int): String {
        val fallback = "Error del servidor. Código: $statusCode"
        return try {
            val root = Json.parseToJsonElement(responseBody).jsonObject
            root["error"]?.jsonPrimitive?.contentOrNull
                ?: root["message"]?.jsonPrimitive?.contentOrNull
                ?: root["detail"]?.jsonPrimitive?.contentOrNull // formato por defecto de DRF
                ?: fallback
        } catch (_: Throwable) {
            fallback
        }
    }

    override fun with(params: String): IHttpRequest {

        val splitted = params.split(PARAM_DELIMITER)
        this.request.url.parameters.append(splitted[0], splitted[1])
        return this
    }

    override suspend fun <T> response(serializer: KSerializer<T>?): DResponse<T> {
        var resultBodyRequest: StringBuilder = StringBuilder()
        
        // Debug: Log the request URL and method before making the call
        val requestUrl = this.request.url.toString().substringBeforeLast("?appId").substringBeforeLast("&appId")
        val requestMethod = this.request.method.value
        FileUtils.log("🔍 DEBUG REQUEST -> $requestMethod $requestUrl")
        
        return try {
            val httpResponse = httpClient.request(this.request)
            
            // Handle 204 No Content - successful response with no body
            if (httpResponse.status.value == 204) {
                FileUtils.log("✅ DEBUG RESPONSE -> 204 No Content for $requestUrl")
                return DResponse.Complete()
            }
            
            resultBodyRequest.append(httpResponse.bodyAsText())
            val statusCode = httpResponse.status.value
            FileUtils.log("📥 DEBUG RESPONSE -> $statusCode for $requestUrl | Body: ${resultBodyRequest.toString().take(500)}")
            
            if(serializer == null){
                DResponse.Complete()
            }else{
                try {
                    val decoded = json.decodeFromString(serializer, resultBodyRequest.toString())
                    DResponse.Success(decoded)
                } catch (se: SerializationException) {
                    FileUtils.log("❌ DEBUG SERIALIZATION ERROR -> Failed to parse response for $requestUrl | Status: $statusCode | Body: ${resultBodyRequest.toString().take(500)}")
                    FileUtils.log("   Exception: ${se.message}")
                    se.printStackTrace()
                    DResponse.Error("Error de serialización. Código: $statusCode")
                }
            }
        } catch (pipeline: ReceivePipelineException) {
            val url = this.request.url.toString().substringBeforeLast("?appId").substringBeforeLast("&appId")
            FileUtils.log("❌ DEBUG NETWORK ERROR -> Failed to reach server at $url")
            FileUtils.log("   Exception type: ${pipeline::class.simpleName}")
            FileUtils.log("   Message: ${pipeline.message}")
            pipeline.printStackTrace()
            DResponse.Error("Error de red. No se puede conectar con el servidor.")
        } catch (serializationException: SerializationException) {
            val url = this.request.url.toString().substringBeforeLast("?appId").substringBeforeLast("&appId")
            FileUtils.log("❌ DEBUG SERIALIZATION ERROR -> Failed to parse response for $url")
            FileUtils.log("   Exception: ${serializationException.message}")
            serializationException.printStackTrace()
            DResponse.Error("Error de serialización. Código: 0")
        // ResponseException cubre 4xx (ClientRequestException) y 5xx (ServerResponseException):
        // si solo se captura el 4xx, el 5xx cae al Throwable genérico y el message de Ktor
        // arrastra el cuerpo de la respuesta hasta la UI.
        } catch (e: ResponseException) {
            val responseBody = try { e.response.bodyAsText() } catch (inner: Throwable) { "Unable to read response body" }
            val url = this.request.url.toString().substringBeforeLast("?appId").substringBeforeLast("&appId")
            FileUtils.log("❌ DEBUG HTTP ERROR -> ${e.response.status.value} for $url")
            FileUtils.log("   Response body: $responseBody")
            val backendMessage = extractBackendErrorMessage(responseBody, e.response.status.value)
            DResponse.Error(
                error = backendMessage,
                throwable = e,
                statusCode = e.response.status.value,
                body = responseBody
            )
        } catch (e: CancellationException) {
            val url = this.request.url.toString().substringBeforeLast("?appId").substringBeforeLast("&appId")
            FileUtils.log("⚠️ DEBUG REQUEST CANCELLED -> $url | ${e.message}")
            DResponse.Error(error="Request cancelled", throwable = e)
        } catch (e: Throwable) {
            val url = this.request.url.toString().substringBeforeLast("?appId").substringBeforeLast("&appId")
            FileUtils.log("❌ DEBUG UNEXPECTED ERROR -> $url | ${e::class.simpleName}")
            FileUtils.log("   Message: ${e.message}")
            e.printStackTrace()
            DResponse.Error(error="Error inesperado: ${e.message ?: "Unknown"}", throwable = e)
        }
    }
}
